import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  IconButton,
  useTheme,
  useMediaQuery,
  Fade
} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import background1 from '../assets/images/background-1.jpg';
import background2 from '../assets/images/background-2.jpg';
import background3 from '../assets/images/background-3.jpg';
import background4 from '../assets/images/background-4.jpg';

// Imágenes para el carrusel con textos específicos para cada slide
const carouselImages = [
  { 
    src: background4, 
    alt: "Jeshua",
    texts: [],
    timeout: 5000
  },
  { 
    src: background1, 
    alt: "Jeshua",
    texts: [
      "\"El que tenga oidos, que escuche...... El que tenga el corazón abierto, que sienta...\"",
      "\"Con mucho amor... Jeshua\""
    ],
    timeout: 10000
  },
  { 
    src: background2, 
    alt: "Jeshua",
    texts: [
      "\"Gracias a canalizadores, maestros, sanadores, terapeutas, músicos y en especial a los trabajadores de la luz, que vida tras vida han colaborado en el proceso de ascensión de la tierra\"",
      "\"Tu amigo y hermano... Jeshua\""
    ],
    timeout: 15000
  },
  { 
    src: background3, 
    alt: "Jeshua",
    texts: [
      "\"Libérense y ábranse más a la luz de su alma... sientan como irradia el sol que son... esa luz impercedera es lo que son... por eso los saludo como hermanos y hermanas y confío en ustedes como pioneros de un nuevo tiempo en la tierra\"",
      "\"Con mucho amor y cariño... Jeshua\""
    ],
    timeout: 20000
  }
];

function Home() {
  const [activeSlide, setActiveSlide] = useState(0);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Avance automático de diapositivas
  useEffect(() => {
    const currentTimeout = carouselImages[activeSlide].timeout;
    const interval = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % carouselImages.length);
    }, currentTimeout);
    
    return () => clearInterval(interval);
  }, [activeSlide]);
  
  const handlePrev = () => {
    setActiveSlide((prev) => (prev === 0 ? carouselImages.length - 1 : prev - 1));
  };
  
  const handleNext = () => {
    setActiveSlide((prev) => (prev + 1) % carouselImages.length);
  };
  
  return (
    <Box sx={{ width: '100%' }}>
      {/* Contenedor del Carrusel con proporción de aspecto 16:9 en móvil */}
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          ...(isMobile ? {
            height: 'auto',
            paddingTop: '56.25%',
          } : {
            height: '90vh',
            paddingTop: 0,
          }),
          overflow: 'hidden',
        }}
      >
        {/* Diapositivas del Carrusel */}
        {carouselImages.map((slide, index) => (
          <Box
            key={index}
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              opacity: activeSlide === index ? 1 : 0,
              transition: 'opacity 1s ease-in-out',
              zIndex: activeSlide === index ? 1 : 0,
            }}
          >
            <Box
              component="img"
              src={slide.src}
              alt={slide.alt}
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                objectPosition: 'center top',
              }}
            />
            
            {/* Superposición con Texto específico para cada slide */}
            <Fade in={activeSlide === index} timeout={1000}>
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  backgroundColor: slide.texts.length > 0 ? 'rgba(0, 0, 0, 0.5)' : 'rgba(0, 0, 0, 0.2)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 4,
                }}
              >
                <Box
                  sx={{
                    width: { 
                      xs: 'calc(100% - 40px)',
                      sm: 'calc(100% - 120px)'
                    },
                    mx: 'auto',
                  }}
                >
                  {slide.texts.map((text, textIndex) => (
                    <Typography
                      key={textIndex}
                      variant="h3"
                      component="h1"
                      sx={{
                        color: 'white',
                        textAlign: 'center',
                        fontFamily: "'Ink Free', Arial, sans-serif",
                        fontSize: { xs: '0.8rem', md: '1.5rem' },
                        textTransform: 'uppercase',
                        my: 2,
                      }}
                      dangerouslySetInnerHTML={{ __html: text }}
                    />
                  ))}
                </Box>
              </Box>
            </Fade>
          </Box>
        ))}
        
        {/* Controles de Navegación */}
        <IconButton
          onClick={handlePrev}
          aria-label="Anterior diapositiva"
          sx={{
            position: 'absolute',
            left: 16,
            top: '50%',
            transform: 'translateY(-50%)',
            color: 'white',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
            },
            zIndex: 3,
            display: isMobile ? 'none' : 'flex',
          }}
        >
          <ArrowBackIosNewIcon />
        </IconButton>
        <IconButton
          onClick={handleNext}
          aria-label="Siguiente diapositiva"
          sx={{
            position: 'absolute',
            right: 16,
            top: '50%',
            transform: 'translateY(-50%)',
            color: 'white',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
            },
            zIndex: 3,
            display: isMobile ? 'none' : 'flex',
          }}
        >
          <ArrowForwardIosIcon />
        </IconButton>
        
        {/* Indicadores de Diapositivas */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 16,
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            gap: 1,
            zIndex: 3
          }}
        >
          {carouselImages.map((_, index) => (
            <Box
              key={index}
              onClick={() => setActiveSlide(index)}
              sx={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                backgroundColor: activeSlide === index ? '#ff5722' : 'white',
                cursor: 'pointer',
                transition: 'background-color 0.3s',
              }}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );
}

export default Home;